import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { authAPI, getFundingToken, getToken, setOtherDeviceLoginHandler, setAuthInvalidHandler } from '@/utils/api';
import { getAuthLandingPath, roleToPortal } from '@/utils/authPortalClient';
import { isIpeLoginPath, isIpeWorkbenchPath } from '@/config/ipeRoutes';
import { showToast } from '@/utils/toast';
import { setViewLoadRateLimitUserScope } from '@/utils/viewLoadRateLimitGuard';

const SESSION_KEY = 'react-basic-session';

const AuthContext = createContext(null);
const normalizeManagementWorkspaceScope = (raw) => String(raw || '').trim();

const readSession = () => {
  try {
    const stored = localStorage.getItem(SESSION_KEY);
    if (!stored) return null;
    const j = JSON.parse(stored);
    if (j.loginPortal !== 'management' && j.loginPortal !== 'user' && j.role) {
      return { ...j, loginPortal: roleToPortal(j.role) };
    }
    return j;
  } catch (error) {
    console.error('读取会话失败:', error);
    return null;
  }
};

const sessionFieldsFromUser = (u) => ({
  id: u._id != null ? String(u._id) : u.id != null ? String(u.id) : '',
  phone: u.phone,
  role: u.role || '普通',
  company: (u.company && String(u.company).trim()) || '',
  nickname: (u.nickname && String(u.nickname).trim()) || '',
  authorizedBy: u.authorizedBy != null ? String(u.authorizedBy) : '',
  fundingToolAuthorized: !!u.fundingToolAuthorized,
  authorizationTypes: Array.isArray(u.authorizationTypes) ? u.authorizationTypes : [],
  hasPassword: u.hasPassword !== false,
  modulePermissions: u.modulePermissions && typeof u.modulePermissions === 'object' ? u.modulePermissions : {},
  teamModulePermissionStrict: !!u.teamModulePermissionStrict,
  managementWorkspaceScope: normalizeManagementWorkspaceScope(u.managementWorkspaceScope),
});

const phonePattern = /^1[3-9]\d{9}$/;
const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[\x20-\x7E]{8,18}$/;

export function AuthProvider({ children }) {
  const [session, setSession] = useState(() => readSession());
  const [loading, setLoading] = useState(true);
  const [showOtherDeviceModal, setShowOtherDeviceModal] = useState(false);

  useEffect(() => {
    setViewLoadRateLimitUserScope(session?.id || null);
  }, [session?.id]);

  // 单设备登录：收到 401 OTHER_DEVICE_LOGGED_IN 时清除会话并弹出提示
  useEffect(() => {
    setOtherDeviceLoginHandler(() => {
      authAPI.logout();
      localStorage.removeItem(SESSION_KEY);
      setSession(null);
      setShowOtherDeviceModal(true);
    });
    return () => setOtherDeviceLoginHandler(null);
  }, []);

  const getLandingUrl = () => {
    const base = (typeof process !== 'undefined' && process.env?.PUBLIC_URL) ? process.env.PUBLIC_URL : '';
    const path = getAuthLandingPath();
    return `${base}${path}`.replace(/\/+/g, '/');
  };

  const goToLogin = () => {
    setShowOtherDeviceModal(false);
    window.location.href = getLandingUrl();
  };

  // 主系统认证失效（401 等）：清除登录态并强制跳转登录页，避免界面仍显示已登录但接口报未提供认证令牌
  useEffect(() => {
    setAuthInvalidHandler(() => {
      authAPI.logout();
      localStorage.removeItem(SESSION_KEY);
      setSession(null);
      if (typeof window !== 'undefined') {
        const pathname = window.location.pathname || '';
        if (isIpeLoginPath(pathname) || pathname === '/login') {
          return;
        }
      }
      const base = (typeof process !== 'undefined' && process.env?.PUBLIC_URL) ? process.env.PUBLIC_URL : '';
      const target = `${base}${getAuthLandingPath()}`.replace(/\/+/g, '/');
      window.location.replace(target);
    });
    return () => setAuthInvalidHandler(null);
  }, []);

  // 初始化时检查 token 是否有效；仅认证失败（401 等）时清 session，网络错误时保留缓存避免反复请求
  useEffect(() => {
    const initAuth = async () => {
      const token = getToken();
      if (!token) {
        setLoading(false);
        return;
      }
      // 已用 IPE funding 登录时，勿拿旧 auth_token 请求 /auth/me（会 401 并误踢回登录页）
      if (
        typeof window !== 'undefined'
        && getFundingToken()
        && (isIpeWorkbenchPath(window.location.pathname) || isIpeLoginPath(window.location.pathname))
      ) {
        setLoading(false);
        return;
      }
      try {
        const data = await authAPI.getCurrentUser();
        if (data.user) {
          const u = data.user;
          const sessionData = {
            ...sessionFieldsFromUser(u),
            loginPortal: roleToPortal(u.role),
            loginAt: Date.now(),
          };
          setSession(sessionData);
          try {
            localStorage.setItem(SESSION_KEY, JSON.stringify(sessionData));
          } catch (e) {
            // ignore
          }
        }
      } catch (error) {
        const msg = (error && error.message) || '';
        const isAuthError = /401|未授权|无效|令牌|用户不存在|其他设备登录/.test(String(msg));
        if (isAuthError) {
          authAPI.logout();
          setSession(null);
          localStorage.removeItem(SESSION_KEY);
          if (/其他设备登录/.test(String(msg))) setShowOtherDeviceModal(true);
        }
        // 网络错误等保留已有 session，不反复清空
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = async (phone, password, extra) => {
    try {
      const data = await authAPI.login(phone, password, extra);
      const u = data.user;
      const sessionData = {
        ...sessionFieldsFromUser(u),
        loginPortal: roleToPortal(u.role),
        loginAt: Date.now(),
      };
      setSession(sessionData);
      localStorage.setItem(SESSION_KEY, JSON.stringify(sessionData));
      if (data && data.accountDeletionCancelled === true) {
        setTimeout(() => {
          showToast('当前注销账号已撤销，请重新提交注销申请。', 'warning', 3500);
        }, 50);
      }
      return data;
    } catch (error) {
      throw new Error(error.message || '登录失败');
    }
  };

  const logout = () => {
    authAPI.logout();
    localStorage.removeItem(SESSION_KEY);
    setSession(null);
    try {
      localStorage.removeItem('distributor_countdown_reminder_last_token');
    } catch (e) {
      /* ignore */
    }
    try {
      localStorage.removeItem('distributor_countdown_modal_auto_last_token');
    } catch (e) {
      /* ignore */
    }
    try {
      sessionStorage.removeItem('distributor_countdown_modal_dismissed_session');
    } catch (e) {
      /* ignore */
    }
  };

  /** 退出登录并强制跳转登录页（整页跳转，避免路由/状态未更新导致仍显示在系统内） */
  const logoutAndRedirectToLogin = () => {
    const base = (typeof process !== 'undefined' && process.env?.PUBLIC_URL) ? process.env.PUBLIC_URL : '';
    const target = `${base}${getAuthLandingPath()}`.replace(/\/+/g, '/');
    authAPI.logout();
    localStorage.removeItem(SESSION_KEY);
    setSession(null);
    try {
      localStorage.removeItem('distributor_countdown_reminder_last_token');
    } catch (e) {
      /* ignore */
    }
    try {
      localStorage.removeItem('distributor_countdown_modal_auto_last_token');
    } catch (e) {
      /* ignore */
    }
    try {
      sessionStorage.removeItem('distributor_countdown_modal_dismissed_session');
    } catch (e) {
      /* ignore */
    }
    window.location.replace(target);
  };

  const register = async (phone, password, nickname, smsCode, extra) => {
    try {
      const data = await authAPI.register(phone, password, nickname, smsCode);
      const u = data.user;
      const sessionData = {
        ...sessionFieldsFromUser(u),
        loginPortal: 'user',
        hasPassword: true,
        loginAt: Date.now(),
      };
      setSession(sessionData);
      localStorage.setItem(SESSION_KEY, JSON.stringify(sessionData));
      return data.user;
    } catch (error) {
      throw new Error(error.message || '注册失败');
    }
  };

  const getCurrentUser = useCallback(async () => {
    try {
      const data = await authAPI.getCurrentUser();
      if (data.user) {
        const u = data.user;
        setSession((prev) => {
          const base = sessionFieldsFromUser(u);
          const next = prev
            ? { ...prev, ...base }
            : { ...base, loginAt: Date.now() };
          try {
            localStorage.setItem(SESSION_KEY, JSON.stringify(next));
          } catch (e) {
            // ignore
          }
          return next;
        });
        return data.user;
      }
      return null;
    } catch (error) {
      return null;
    }
  }, []);

  const updatePassword = async (payload) => {
    if (!session?.phone) {
      throw new Error('当前未登录');
    }
    if (!passwordPattern.test(payload.newPassword)) {
      throw new Error('新密码需8-18位，至少包含1个大写字母、1个小写字母、1个数字，可包含特殊字符（如@、-）');
    }
    try {
      await authAPI.updatePassword(payload);
      setSession((prev) => {
        const next = prev ? { ...prev, hasPassword: true } : prev;
        if (next) {
          try {
            localStorage.setItem(SESSION_KEY, JSON.stringify(next));
          } catch (e) {
            // ignore
          }
        }
        return next;
      });
      return { success: true };
    } catch (error) {
      throw new Error(error.message || '更新密码失败');
    }
  };

  const updatePhone = async ({ newPhone, code }) => {
    if (!session?.phone) {
      throw new Error('当前未登录');
    }
    if (!phonePattern.test(newPhone)) {
      throw new Error('请输入正确的11位中国大陆手机号');
    }
    if (newPhone === session.phone) {
      throw new Error('新手机号不能与当前手机号相同');
    }
    try {
      const data = await authAPI.updatePhone(newPhone, code);
      const u = data.user;
      const sessionData = {
        ...sessionFieldsFromUser(u),
        loginAt: Date.now(),
      };
      setSession(sessionData);
      localStorage.setItem(SESSION_KEY, JSON.stringify(sessionData));
      return sessionData;
    } catch (error) {
      throw new Error(error.message || '更新手机号失败');
    }
  };

  const deleteAccount = async () => {
    if (!session?.phone) {
      throw new Error('当前未登录');
    }
    try {
      await authAPI.deleteAccount();
      authAPI.logout();
      localStorage.removeItem(SESSION_KEY);
      setSession(null);
      return { success: true };
    } catch (error) {
      throw new Error(error.message || '注销账号失败');
    }
  };

  const value = useMemo(
    () => ({
      isAuthenticated: Boolean(session),
      session,
      loading,
      login,
      logout,
      logoutAndRedirectToLogin,
      register,
      getCurrentUser,
      updatePassword,
      updatePhone,
      deleteAccount,
    }),
    [session, loading]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      {showOtherDeviceModal && (
        <div className="auth-other-device-overlay" role="dialog" aria-modal="true" aria-labelledby="other-device-modal-title">
          <div className="auth-other-device-modal">
            <h2 id="other-device-modal-title" className="auth-other-device-modal__title">当前账号已在其他设备登录</h2>
            <p className="auth-other-device-modal__text">账号会话已失效，将返回 IPE 工作台（无需主系统登录页）</p>
            <div className="auth-other-device-modal__actions">
              <button type="button" className="auth-other-device-modal__btn auth-other-device-modal__btn--primary" onClick={goToLogin}>
                返回工作台
              </button>
              <button type="button" className="auth-other-device-modal__btn auth-other-device-modal__btn--secondary" onClick={goToLogin}>
                关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth 必须在 AuthProvider 中使用');
  }
  return context;
};
